import assert from 'minimalistic-assert';
import parseStackTraceNatively, { stackFramesToString } from './stack.js';
import { omitUndefined } from './utils.js';
function makeReadonlySet(values) {
    return new Set(values);
}
const SAFE_CODES_SET = makeReadonlySet(['EPIPE']);
const SAFE_NAMES_SET = makeReadonlySet([
    'Error',
    'AssertionError',
    'TypeError',
    'RangeError',
    'UnknownError',
    'SafeErrorFailedToParse',
    'TimeoutError',
]);
const safeHints = {
    __proto__: null,
    broadcastTx: {
        general: 'broadcastTx',
        other: 'otherErr:broadcastTx',
        retry: 'retry:broadcastTx',
    },
    getNftArguments: {
        general: 'getNftArguments',
    },
    ethCall: {
        general: 'ethCall',
        executionReverted: 'ethCall:executionReverted',
    },
    safeError: {
        failedToParse: 'failed to parse error',
    },
};
const SAFE_HINTS_SET = makeReadonlySet(
// @ts-expect-error doesn't like __proto__: null
Object.values(safeHints).flatMap((safeHintCategory) => Object.values(safeHintCategory)));
function getSafeHint(value) {
    if (typeof value !== 'string') {
        return undefined;
    }
    if (value === '') {
        return undefined;
    }
    if (isSafeHint(value)) {
        return value;
    }
    return [...SAFE_HINTS_SET].find((sh) => value.includes(sh));
}
function isSafeHint(value) {
    return SAFE_HINTS_SET.has(value);
}
function isSafeName(value) {
    return SAFE_NAMES_SET.has(value);
}
function isExodusErrorCode(code) {
    return /^(EXOD-)?\d{1,4}$/u.test(code);
}
function isSafeCode(value) {
    return SAFE_CODES_SET.has(value) || isExodusErrorCode(value);
}
const FACTORY_SYMBOL = Symbol('SafeError');
export class SafeError {
    static hints = safeHints;
    static from(err) {
        let safeName;
        let safeCode;
        let safeHint;
        try {
            const { name, message, hint, code } = err;
            safeName = isSafeName(name) ? name : 'UnknownError';
            const safeCodeCandidate = `${code}`;
            safeCode = isSafeCode(safeCodeCandidate) ? safeCodeCandidate : undefined;
            safeHint = getSafeHint(hint) || getSafeHint(message);
            const safeStack = parseStackTraceNatively(err);
            return new SafeError({
                name: safeName,
                code: safeCode,
                hint: safeHint,
                stack: safeStack,
                initSymbol: FACTORY_SYMBOL,
            });
        }
        catch {
            try {
                return safeName
                    ? new SafeError({
                        name: safeName,
                        code: safeCode,
                        hint: safeHint,
                        initSymbol: FACTORY_SYMBOL,
                    })
                    : FAILED_TO_PARSE_ERROR;
            }
            catch {
                return FAILED_TO_PARSE_ERROR;
            }
        }
    }
    #name;
    #code;
    #hint;
    #stackFrames;
    #timestamp;
    get name() {
        return this.#name;
    }
    get code() {
        return this.#code;
    }
    get hint() {
        return this.#hint;
    }
    get stackFrames() {
        return this.#stackFrames?.map((frame) => ({ __proto__: null, ...frame }));
    }
    get stack() {
        const stackTrace = stackFramesToString(this.stackFrames);
        return stackTrace
            ? `${this.name}: ${this.code || this.hint || 'unknownHint'}\n${stackTrace}`
            : undefined;
    }
    get timestamp() {
        return this.#timestamp;
    }
    toJSON() {
        return omitUndefined({
            __proto__: null,
            name: this.name,
            code: this.code,
            hint: this.hint,
            stack: this.stack,
            timestamp: this.timestamp,
        });
    }
    constructor({ name, code, hint, stack, initSymbol, }) {
        assert(initSymbol === FACTORY_SYMBOL, 'SafeError: use SafeError.from()');
        this.#name = name;
        this.#code = code;
        this.#hint = hint;
        this.#stackFrames = stack?.map((frame) => ({ ...frame }));
        this.#timestamp = Date.now();
    }
}
const FAILED_TO_PARSE_ERROR = new SafeError({
    initSymbol: FACTORY_SYMBOL,
    name: 'SafeErrorFailedToParse',
});
